import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router";
import { api, errorMessage, isCode } from "../api/client.ts";
import type { Brand, Member, RoleItem, RolesData, TeamData } from "../api/types.ts";
import { CodeInput } from "../components/CodeInput.tsx";
import { DataTable, type Column } from "../components/DataTable.tsx";
import { Dialog } from "../components/Dialog.tsx";
import { IconPlus } from "../components/Icons.tsx";
import { StepUpDialog } from "../components/StepUp.tsx";
import { useToast } from "../components/Toast.tsx";
import { Alert, CopyButton, EmptyState, PageHeader, Spinner, StatusPill, Time } from "../components/ui.tsx";
import { formatTimeFull, timeAgo } from "../lib/format.ts";
import { useAsync } from "../lib/hooks.ts";
import { useClientPages } from "../lib/paging.ts";
import { Pager } from "../components/Pager.tsx";
import { useMe, useSession } from "../lib/session.tsx";
import { RolesPanel, RoleEditor, roleDescription, roleName } from "./Roles.tsx";
import { fmt, plural, rich, useT } from "../i18n/index.tsx";

const initials = (name: string) => name.split(/\s+/).map((s) => s[0]).slice(0, 2).join("").toUpperCase();
export const roleClass = (key: string | null | undefined) => `role-tag role-${key ?? "custom"}`;

/** "All brands" or the brand names. */
export function BrandScope({ all, ids, brands }: { all: boolean; ids: string[]; brands: Brand[] }) {
  const t = useT();
  if (all) return <span className="scope-all">{t.team.allBrands}</span>;
  const names = ids.map((id) => brands.find((b) => b.id === id)?.name ?? t.team.unknownBrand);
  return (
    <span className="chips">
      {names.map((n) => <span key={n} className="brand-chip">{n}</span>)}
    </span>
  );
}

export function Team() {
  const t = useT();
  const me = useMe();
  const { can } = useSession();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === "roles" ? "roles" : "members";
  const team = useAsync(() => api.get<TeamData>("/team"), []);
  const roles = useAsync(() => api.get<RolesData>("/roles"), []);
  const [inviting, setInviting] = useState(false);
  const [editing, setEditing] = useState<Member | null>(null);
  const [ipFor, setIpFor] = useState<Member | null>(null);
  const [disable, setDisable] = useState<Member | null>(null);
  const [cancel, setCancel] = useState<TeamData["invites"][number] | null>(null);
  const [disabledNote, setDisabledNote] = useState<{ name: string; keys: number } | null>(null);
  const [newRole, setNewRole] = useState(false);
  const toast = useToast();
  const brands = team.data?.brands ?? [];
  const members = team.data?.members ?? [];
  const invites = team.data?.invites ?? [];
  const orgName = team.data?.organization.name ?? me.organization.name;
  const self = (m: Member) => m.user_id === me.user.id;
  const mp = useClientPages(members, "m_");
  const ip = useClientPages(invites, "inv_");

  const cols: Column<Member>[] = [
    {
      key: "name", header: t.team.colMember, mobile: "title", cell: (m) => (
        <span className="member">
          <span className="avatar sm" aria-hidden="true">{initials(m.name)}</span>
          <span className="stack-cell">
            <strong>{m.name} {self(m) && <span className="you-tag">{t.common.you}</span>}</strong>
            <small className="muted">{m.email}</small>
          </span>
        </span>
      ),
    },
    { key: "role", header: t.team.colRole, cell: (m) => <span className={roleClass(m.role.key)}>{roleName(m.role)}</span> },
    { key: "brands", header: t.team.colBrands, cell: (m) => <BrandScope all={m.all_merchants} ids={m.merchant_ids} brands={brands} /> },
    {
      key: "status", header: t.common.status, cell: (m) => (
        <span className="status-cell">
          <StatusPill status={m.status} />
          {m.ip_allowlist !== null && (
            <span className="ip-flag" title={m.ip_allowlist.length ? fmt(t.team.personalIpList, { ips: m.ip_allowlist.join(", ") }) : t.team.personalIpAny}>
              {t.team.ownIpList}
            </span>
          )}
        </span>
      ),
    },
    {
      key: "login", header: t.team.colLastLogin, cell: (m) => m.status === "enrolling"
        ? <span className="muted small">{t.team.no2fa}</span>
        : <span title={m.last_login_at ? formatTimeFull(m.last_login_at) : undefined}>{timeAgo(m.last_login_at)}</span>,
    },
  ];
  const anyAction = can("team:edit") || can("team:disable") || can("console_ip:edit");
  if (anyAction)
    cols.push({
      key: "act", header: t.team.colActions, align: "right", cell: (m) => {
        if (m.status === "disabled") return null;
        const btns: ReactNode[] = [];
        if (can("team:edit") && !self(m)) btns.push(<button key="e" type="button" className="btn btn-sm btn-ghost" onClick={() => setEditing(m)}>{t.common.edit}</button>);
        if (can("console_ip:edit")) btns.push(<button key="i" type="button" className="btn btn-sm btn-ghost" onClick={() => setIpFor(m)}>{t.team.ipList}</button>);
        if (can("team:disable") && !self(m)) btns.push(<button key="d" type="button" className="btn btn-sm btn-ghost btn-danger-ghost" onClick={() => setDisable(m)}>{t.team.disable}</button>);
        return btns.length ? <span className="btn-row tight">{btns}</span> : null;
      },
    });

  const inviteCols: Column<TeamData["invites"][number]>[] = [
    { key: "email", header: t.team.colEmail, cell: (i) => <strong>{i.email}</strong>, mobile: "title" },
    {
      key: "role", header: t.team.colRole, cell: (i) => {
        const r = roles.data?.items.find((x) => x.name === i.role);
        return <span className={roleClass(r?.key)}>{roleName({ key: r?.key, name: i.role })}</span>;
      },
    },
    { key: "brands", header: t.team.colBrands, cell: (i) => <BrandScope all={i.all_merchants} ids={i.merchant_ids} brands={brands} /> },
    { key: "sent", header: t.team.colSent, cell: (i) => <Time iso={i.created_at} /> },
    { key: "exp", header: t.team.colExpires, cell: (i) => timeAgo(i.expires_at) },
  ];
  if (can("team:invite"))
    inviteCols.push({ key: "act", header: t.team.colActions, align: "right", cell: (i) => <button type="button" className="btn btn-sm btn-ghost btn-danger-ghost" onClick={() => setCancel(i)}>{t.team.cancelInvite}</button> });

  const setTab = (next: string) => setParams((p) => {
    const n = new URLSearchParams(p);
    if (next === "roles") n.set("tab", "roles");
    else n.delete("tab");
    return n;
  }, { replace: true });

  return (
    <div className="page">
      <PageHeader
        title={t.team.title}
        eyebrow={orgName}
        actions={
          tab === "members"
            ? can("team:invite") && <button type="button" data-tour="invite" className="btn btn-primary" onClick={() => setInviting(true)}><IconPlus size={18} /> {t.team.inviteMember}</button>
            : can("roles:manage") && <button type="button" className="btn btn-primary" onClick={() => setNewRole(true)}><IconPlus size={18} /> {t.team.newRole}</button>
        }
      >
        {fmt(t.team.intro, { org: orgName })}
      </PageHeader>

      <div className="tabs" role="tablist" aria-label={t.team.viewsLabel}>
        <button type="button" role="tab" aria-selected={tab === "members"} className="tab" onClick={() => setTab("members")}>
          {t.team.tabMembers} {team.data && <span className="tab-num">{members.filter((m) => m.status !== "disabled").length}</span>}
        </button>
        <button type="button" role="tab" aria-selected={tab === "roles"} className="tab" onClick={() => setTab("roles")}>
          {t.team.tabRoles} {roles.data && <span className="tab-num">{roles.data.items.length}</span>}
        </button>
      </div>

      {tab === "roles" ? (
        <RolesPanel q={roles} />
      ) : (
        <>
          {disabledNote && disabledNote.keys > 0 && (
            <div className="section-gap">
              <Alert tone="warn" title={fmt(t.team.disabledTitle, { name: disabledNote.name })} action={<button type="button" className="btn btn-ghost btn-sm" onClick={() => setDisabledNote(null)}>{t.team.dismiss}</button>}>
                {rich(plural(disabledNote.keys, t.team.disabledKeys), { a: (c) => <Link to="/developers">{c}</Link> })}
              </Alert>
            </div>
          )}
          {team.error ? (
            <Alert tone="error" action={<button className="btn btn-ghost btn-sm" onClick={() => void team.reload()}>{t.common.retry}</button>}>{errorMessage(team.error)}</Alert>
          ) : (
            <>
              <section aria-label={t.team.membersLabel}>
                <DataTable caption={t.team.membersCaption} columns={cols} rows={mp.rows} rowKey={(m) => m.id} loading={team.loading}
                  footer={members.length > 0 && <Pager page={mp.page} limit={mp.limit} count={mp.rows.length} total={mp.total} onPage={mp.setPage} onLimit={mp.setLimit} />} />
              </section>
              {(invites.length > 0 || can("team:invite")) && !team.loading && (
                <section className="section-block" aria-labelledby="inv-h">
                  <div className="section-head"><h2 id="inv-h">{t.team.pendingInvites}</h2></div>
                  <DataTable caption={t.team.pendingInvites} columns={inviteCols} rows={ip.rows} rowKey={(i) => i.id}
                    footer={invites.length > 0 && <Pager page={ip.page} limit={ip.limit} count={ip.rows.length} total={ip.total} onPage={ip.setPage} onLimit={ip.setLimit} />}
                    empty={<EmptyState title={t.team.noInvitesTitle}>{t.team.noInvitesBody}</EmptyState>} />
                </section>
              )}
            </>
          )}
        </>
      )}

      {inviting && <InviteDialog roles={roles.data?.items ?? []} brands={brands} onClose={() => setInviting(false)} onSent={() => void team.reload()} />}
      {editing && (
        <EditMember member={editing} roles={roles.data?.items ?? []} brands={brands} onClose={() => setEditing(null)}
          onSaved={(m) => { toast(fmt(t.team.roleChanged, { name: m.name, role: roleName(m.role) })); void team.reload(); }} />
      )}
      {ipFor && <MemberIpDialog member={ipFor} isSelf={self(ipFor)} onClose={() => setIpFor(null)} onSaved={() => { toast(t.team.ipSaved); void team.reload(); }} />}
      {newRole && <RoleEditor mode="create" allPermissions={roles.data?.permissions ?? []} onClose={() => setNewRole(false)} onSaved={() => { void roles.reload(); setTab("roles"); }} />}

      <StepUpDialog
        open={!!disable}
        onClose={() => setDisable(null)}
        title={fmt(t.team.disableTitle, { name: disable?.name ?? "" })}
        description={disable?.status === "enrolling"
          ? t.team.disableEnrolling
          : fmt(t.team.disableActive, { org: orgName })}
        confirmLabel={t.team.disableMember}
        danger
        onConfirm={async (code) => {
          const r = await api.post<{ ok: boolean; active_keys_created?: number }>(`/team/members/${encodeURIComponent(disable?.id ?? "")}/disable`, { code });
          const keys = r.active_keys_created ?? 0;
          setDisabledNote({ name: disable?.name ?? "", keys });
          if (!keys) toast(fmt(t.team.disabledTitle, { name: String(disable?.name) }));
          void team.reload();
        }}
      />
      <StepUpDialog
        open={!!cancel}
        onClose={() => setCancel(null)}
        title={t.team.cancelInviteTitle}
        description={<>{rich(t.team.cancelInviteBody, {}, { email: cancel?.email ?? "" })}</>}
        confirmLabel={t.team.cancelInvite}
        danger
        needsCode={false}
        onConfirm={async () => {
          await api.del(`/team/invites/${encodeURIComponent(cancel?.id ?? "")}`);
          toast(t.team.inviteCancelled);
          void team.reload();
        }}
      />
    </div>
  );
}

// ---------------- shared form bits ----------------

function RoleSelect({ roles, value, onChange, id }: { roles: RoleItem[]; value: string; onChange: (id: string) => void; id: string }) {
  const t = useT();
  const r = roles.find((x) => x.id === value);
  const desc = r && roleDescription(r);
  return (
    <div className="field">
      <label htmlFor={id}>{t.team.role}</label>
      <div className="select-wrap">
        <select id={id} value={value} onChange={(e) => onChange(e.target.value)}>
          {!roles.length && <option value="">{t.team.loadingOption}</option>}
          {roles.map((x) => <option key={x.id} value={x.id}>{x.built_in ? roleName(x) : fmt(t.team.customRole, { name: x.name })}</option>)}
        </select>
      </div>
      {desc && <p className="field-hint">{desc}</p>}
    </div>
  );
}

function BrandPicker({ brands, all, ids, onChange }: { brands: Brand[]; all: boolean; ids: string[]; onChange: (all: boolean, ids: string[]) => void }) {
  const t = useT();
  return (
    <fieldset className="field brand-picker">
      <legend>{t.team.brands}</legend>
      <label className="check">
        <input type="checkbox" checked={all} onChange={(e) => onChange(e.target.checked, ids)} />
        <span>
          <strong>{t.team.allBrandsFuture}</strong>
          <small>{t.team.allBrandsFutureHint}</small>
        </span>
      </label>
      {!all && (
        <div className="brand-list">
          {brands.map((b) => (
            <label key={b.id} className="check">
              <input type="checkbox" checked={ids.includes(b.id)} onChange={(e) => onChange(false, e.target.checked ? [...ids, b.id] : ids.filter((x) => x !== b.id))} />
              <span><strong>{b.name}</strong></span>
            </label>
          ))}
        </div>
      )}
    </fieldset>
  );
}

const parseIps = (s: string) => s.split(/[\s,;]+/).map((x) => x.trim()).filter(Boolean);

function useStepForm() {
  const t = useT();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [codeError, setCodeError] = useState<string | null>(null);
  const run = async (fn: () => Promise<void>) => {
    if (code.length !== 6) return setCodeError(t.team.codeRequired);
    setBusy(true);
    setError(null);
    setCodeError(null);
    try {
      await fn();
    } catch (err) {
      setCode("");
      if (isCode(err, "mfa_required")) setCodeError(t.team.codeFailed);
      else setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  const codeField = <CodeInput value={code} onChange={(v) => { setCode(v); setCodeError(null); }} error={codeError} disabled={busy} />;
  return { code, busy, error, setError, run, codeField };
}

// ---------------- invite ----------------

function InviteDialog({ roles, brands, onClose, onSent }: { roles: RoleItem[]; brands: Brand[]; onClose: () => void; onSent: () => void }) {
  const [email, setEmail] = useState("");
  const [roleId, setRoleId] = useState("");
  const [all, setAll] = useState(true);
  const [ids, setIds] = useState<string[]>([]);
  const [url, setUrl] = useState<string | null>(null);
  const [mail, setMail] = useState<{ sent: boolean; error: string | null } | null>(null);
  const f = useStepForm();
  const t = useT();

  useEffect(() => {
    if (!roleId && roles.length) setRoleId((roles.find((r) => r.key === "viewer") ?? roles[0])?.id ?? "");
  }, [roles, roleId]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) return f.setError(t.team.invalidEmail);
    if (!all && !ids.length) return f.setError(t.team.pickBrand);
    void f.run(async () => {
      const body = all ? { email: email.trim(), role_id: roleId, all_merchants: true, code: f.code } : { email: email.trim(), role_id: roleId, merchant_ids: ids, code: f.code };
      const r = await api.post<{ invite_url: string; email?: { sent: boolean; error: string | null } }>("/team/invites", body);
      setUrl(r.invite_url);
      setMail(r.email ?? null);
      onSent();
    });
  };

  return (
    <Dialog open onClose={onClose} busy={f.busy} title={url ? t.team.inviteReady : t.team.inviteTitle} description={url ? undefined : t.team.inviteDesc}>
      {url ? (
        <div className="form-stack">
          {mail?.sent ? (
            <Alert tone="success" title={fmt(t.team.inviteEmailed, { email: email.trim() })}>{t.team.inviteEmailedBody}</Alert>
          ) : mail ? (
            <Alert tone="warn" title={fmt(t.team.inviteNotEmailed, { email: email.trim() })}>
              {t.team.inviteNotEmailedBody}
              {mail.error && <small className="muted" style={{ display: "block", marginTop: 6 }}>{mail.error}</small>}
            </Alert>
          ) : (
            <Alert tone="success" title={fmt(t.team.inviteCreated, { email: email.trim() })}>{t.team.inviteCreatedBody}</Alert>
          )}
          <div className="secret">
            <div className="secret-label"><span>{t.team.inviteLink}</span><CopyButton value={url} label={t.team.copyLink} showText /></div>
            <code className="secret-value">{url}</code>
          </div>
          <div className="dlg-actions">
            <button type="button" className="btn btn-primary" onClick={onClose} data-autofocus>{t.common.done}</button>
          </div>
        </div>
      ) : (
        <form className="form-stack" onSubmit={submit} noValidate>
          <div className="field">
            <label htmlFor="inv-email">{t.team.email}</label>
            <input id="inv-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="off" placeholder="name@company.com" />
          </div>
          <RoleSelect id="inv-role" roles={roles} value={roleId} onChange={setRoleId} />
          <BrandPicker brands={brands} all={all} ids={ids} onChange={(a, i) => { setAll(a); setIds(i); }} />
          {f.codeField}
          {f.error && <Alert tone="error">{f.error}</Alert>}
          <div className="dlg-actions">
            <button type="button" className="btn btn-ghost" onClick={onClose} disabled={f.busy}>{t.common.cancel}</button>
            <button type="submit" className="btn btn-primary" disabled={f.busy || !roleId}>{f.busy && <Spinner />} {t.team.createInviteLink}</button>
          </div>
        </form>
      )}
    </Dialog>
  );
}

// ---------------- edit role + brands ----------------

function EditMember({ member, roles, brands, onClose, onSaved }: { member: Member; roles: RoleItem[]; brands: Brand[]; onClose: () => void; onSaved: (m: Member) => void }) {
  const [roleId, setRoleId] = useState(member.role.id);
  const [all, setAll] = useState(member.all_merchants);
  const [ids, setIds] = useState<string[]>(member.merchant_ids);
  const f = useStepForm();
  const t = useT();
  const changed = roleId !== member.role.id || all !== member.all_merchants || ids.slice().sort().join() !== member.merchant_ids.slice().sort().join();

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!all && !ids.length) return f.setError(t.team.pickBrand);
    void f.run(async () => {
      const body = all ? { role_id: roleId, all_merchants: true, code: f.code } : { role_id: roleId, all_merchants: false, merchant_ids: ids, code: f.code };
      const m = await api.patch<Member>(`/team/members/${encodeURIComponent(member.id)}`, body);
      onSaved(m);
      onClose();
    });
  };

  return (
    <Dialog open onClose={onClose} busy={f.busy} title={fmt(t.team.editTitle, { name: member.name })} description={t.team.editDesc}>
      <form className="form-stack" onSubmit={submit} noValidate>
        <RoleSelect id="edit-role" roles={roles} value={roleId} onChange={setRoleId} />
        <BrandPicker brands={brands} all={all} ids={ids} onChange={(a, i) => { setAll(a); setIds(i); }} />
        {f.codeField}
        {f.error && <Alert tone="error">{f.error}</Alert>}
        <div className="dlg-actions">
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={f.busy}>{t.common.cancel}</button>
          <button type="submit" className="btn btn-primary" disabled={f.busy || !changed}>{f.busy && <Spinner />} {t.team.saveChanges}</button>
        </div>
      </form>
    </Dialog>
  );
}

// ---------------- personal console IP list ----------------

function MemberIpDialog({ member, isSelf, onClose, onSaved }: { member: Member; isSelf: boolean; onClose: () => void; onSaved: () => void }) {
  const [useOrg, setUseOrg] = useState(member.ip_allowlist === null);
  const [text, setText] = useState((member.ip_allowlist ?? []).join("\n"));
  const f = useStepForm();
  const t = useT();
  const list = parseIps(text);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    void f.run(async () => {
      await api.put(`/team/members/${encodeURIComponent(member.id)}/ip-allowlist`, { ip_allowlist: useOrg ? null : list, code: f.code });
      onSaved();
      onClose();
    });
  };

  return (
    <Dialog open onClose={onClose} busy={f.busy} title={fmt(t.team.ipTitle, { name: member.name })} description={t.team.ipDesc}>
      <form className="form-stack" onSubmit={submit} noValidate>
        <fieldset className="field role-picker">
          <legend>{t.team.networks}</legend>
          <label className={`role-option ${useOrg ? "selected" : ""}`}>
            <input type="radio" name="ipmode" checked={useOrg} onChange={() => setUseOrg(true)} />
            <span><strong>{t.team.useOrgList}</strong><small>{t.team.useOrgListHint}</small></span>
          </label>
          <label className={`role-option ${!useOrg ? "selected" : ""}`}>
            <input type="radio" name="ipmode" checked={!useOrg} onChange={() => setUseOrg(false)} />
            <span><strong>{t.team.personalList}</strong><small>{t.team.personalListHint}</small></span>
          </label>
        </fieldset>
        {!useOrg && (
          <div className="field">
            <label htmlFor="mip">{t.team.ipAddresses}</label>
            <textarea id="mip" rows={4} className="mono-input" value={text} onChange={(e) => setText(e.target.value)} placeholder={"203.0.113.10\n198.51.100.0/24"} spellCheck={false} />
            <p className="field-hint">{t.team.ipHint}</p>
          </div>
        )}
        {isSelf && <Alert tone="warn">{t.team.ownListWarn}</Alert>}
        {f.codeField}
        {f.error && <Alert tone="error">{f.error}</Alert>}
        <div className="dlg-actions">
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={f.busy}>{t.common.cancel}</button>
          <button type="submit" className="btn btn-primary" disabled={f.busy}>{f.busy && <Spinner />} {t.common.save}</button>
        </div>
      </form>
    </Dialog>
  );
}
